import { useEffect, useRef, useState } from 'react';
import { ArrowRight, MapPinned, Menu, Phone, ShieldCheck, X } from 'lucide-react';
import { PHONE_HREF } from '../../lib/contact';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollYRef = useRef(0);

  const handleContactClick = () => {
    if (typeof window === 'undefined') return;

    const phone = PHONE_HREF;

    const w = window as any;
    const consentGranted = w?.__adsConsentGranted === true;

    if (typeof w.gtag === 'function' && consentGranted) {
      w.gtag('event', 'conversion', {
        send_to: 'AW-412662118/6fAYCNjKlYQcEOby4sQB',
      });
    }

    window.location.href = phone;
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (typeof window === 'undefined') return;

      const currentY = window.scrollY || window.pageYOffset;
      const lastY = lastScrollYRef.current;

      const THRESHOLD = 16;

      if (Math.abs(currentY - lastY) < THRESHOLD) {
        return;
      }

      if (currentY > lastY && currentY > 80) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transform transition-transform duration-300 ${
        isHidden ? '-translate-y-full' : 'translate-y-0'
      }`}
    >
      <div className="mx-auto max-w-6xl px-4 pt-4">
        <div className="flex items-center justify-between gap-4 rounded-[1.1rem] border border-[#2f2720] bg-[#161311]/96 px-4 py-3 shadow-[0_18px_50px_rgba(15,10,6,0.55)] backdrop-blur md:px-6 md:py-4">
          <a href="/" aria-label="Startseite Abschleppdienst Schiek" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-300/40 bg-[#241d17] text-amber-300">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-lg font-semibold tracking-[0.18em] text-[#f7f1e7]">
                SCHIEK
              </span>
              <span className="mt-0.5 text-[0.66rem] text-[#d8c7b3]">
                Abschleppdienst Berlin, 24/7 direkt erreichbar
              </span>
            </div>
          </a>

          <div className="hidden items-center gap-6 md:flex">
            <nav className="flex items-center gap-5 text-sm text-[#e7ddd1]">
              <a
                href="/#hero"
                className="transition-colors hover:text-amber-300"
              >
                Start
              </a>
              <a
                href="/leistungen"
                className="transition-colors hover:text-amber-300"
              >
                Leistungen
              </a>
              <a
                href="/notruf"
                className="transition-colors hover:text-amber-300"
              >
                Notruf
              </a>
              <a
                href="/einsatzgebiete"
                className="transition-colors hover:text-amber-300"
              >
                Einsatzgebiete
              </a>
              <a
                href="/about"
                className="transition-colors hover:text-amber-300"
              >
                Über uns
              </a>
              <a
                href="/kontakt"
                className="transition-colors hover:text-amber-300"
              >
                Kontakt
              </a>
            </nav>

            <button
              type="button"
              onClick={handleContactClick}
              className="inline-flex items-center gap-2 rounded-full border border-amber-300/45 bg-[#f2bf63] px-5 py-2 text-sm font-semibold text-[#1c140d] transition-colors hover:bg-[#ffcf82]"
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              Jetzt anrufen
            </button>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <button
              className="flex min-h-11 min-w-11 items-center justify-center rounded-full border border-[#3a3128] bg-[#241d17] text-[#f5eadb] transition-colors hover:border-amber-300/60"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-label="Menü"
            >
              {isMenuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`md:hidden px-4 pb-3 overflow-hidden transition-all duration-250 ease-out ${
          isMenuOpen ? 'max-h-[30rem] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="mx-auto max-w-6xl">
          <div className="mt-2 rounded-[1.1rem] border border-[#2f2720] bg-[#161311] px-5 py-5 shadow-[0_18px_40px_rgba(15,10,6,0.48)]">
            <a
              href={PHONE_HREF}
              className="mb-4 flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#f2bf63] px-4 text-sm font-semibold text-[#1c140d]"
              onClick={() => setIsMenuOpen(false)}
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              Notruf direkt anrufen
            </a>
            <div className="grid gap-3">
              <a
                href="/#hero"
                className="flex items-center justify-between rounded-2xl border border-[#2f2720] px-4 py-3 text-sm text-[#f5eadb] transition-colors hover:border-amber-300/50 hover:text-amber-300"
                onClick={() => setIsMenuOpen(false)}
              >
                <span>Start</span>
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="/leistungen"
                className="flex items-center justify-between rounded-2xl border border-[#2f2720] px-4 py-3 text-sm text-[#f5eadb] transition-colors hover:border-amber-300/50 hover:text-amber-300"
                onClick={() => setIsMenuOpen(false)}
              >
                <span>Leistungen</span>
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="/notruf"
                className="flex items-center justify-between rounded-2xl border border-[#2f2720] px-4 py-3 text-sm text-[#f5eadb] transition-colors hover:border-amber-300/50 hover:text-amber-300"
                onClick={() => setIsMenuOpen(false)}
              >
                <span>Notruf</span>
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="/einsatzgebiete"
                className="flex items-center justify-between rounded-2xl border border-[#2f2720] px-4 py-3 text-sm text-[#f5eadb] transition-colors hover:border-amber-300/50 hover:text-amber-300"
                onClick={() => setIsMenuOpen(false)}
              >
                <span>Einsatzgebiete</span>
                <MapPinned className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="/about"
                className="flex items-center justify-between rounded-2xl border border-[#2f2720] px-4 py-3 text-sm text-[#f5eadb] transition-colors hover:border-amber-300/50 hover:text-amber-300"
                onClick={() => setIsMenuOpen(false)}
              >
                <span>Über uns</span>
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="/kontakt"
                className="flex items-center justify-between rounded-2xl border border-[#2f2720] px-4 py-3 text-sm text-[#f5eadb] transition-colors hover:border-amber-300/50 hover:text-amber-300"
                onClick={() => setIsMenuOpen(false)}
              >
                <span>Kontakt</span>
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
